import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Library } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Card } from '@/components/common/Card';
import { Modal } from '@/components/common/Modal';
import { PageHeader } from '@/components/layout/PageHeader';
import { TextField } from '@/components/forms/Field';
import { DataTable, type Column } from '@/components/tables/DataTable';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { salaryComponentsApi, salaryStructuresApi } from '@/services/api/endpoints/hr';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import type { SalaryStructure } from '@/types/hr';
import { formatRate } from '@/utils/format';
import { Badge, Money, SalaryStatusBadge } from './shared';

interface LineDraft {
  componentId: string;
  included: boolean;
  value: string;
  sequence: number;
}

/**
 * Salary packages.
 *
 * <p>The component list is edited as a whole and submitted in one go, which is
 * what the server expects: a package's lines are replaced wholesale rather than
 * patched. Statutory components cannot be unticked, because the server refuses a
 * package that drops one and it is better to say so than to let somebody
 * discover it on submit.
 */
export function SalaryStructuresPage() {
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();
  const manages = can(PERMISSIONS.SALARY_STRUCTURE_MANAGE);

  const [editing, setEditing] = useState<SalaryStructure | null>(null);
  const [creating, setCreating] = useState(false);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [minBasic, setMinBasic] = useState('');
  const [maxBasic, setMaxBasic] = useState('');
  const [lines, setLines] = useState<LineDraft[]>([]);

  const structures = useQuery({
    queryKey: queryKeys.salaryStructures.list(false),
    queryFn: () => salaryStructuresApi.list(false),
  });

  const components = useQuery({
    queryKey: queryKeys.salaryComponents.list(true),
    queryFn: () => salaryComponentsApi.list(true),
  });

  const componentsById = useMemo(() => {
    const map = new Map((components.data ?? []).map((component) => [component.id, component]));
    return map;
  }, [components.data]);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.salaryStructures.all });
  };

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        code,
        name,
        description: description.trim() === '' ? undefined : description.trim(),
        minBasicSalary: minBasic === '' ? null : Number(minBasic),
        maxBasicSalary: maxBasic === '' ? null : Number(maxBasic),
        components: lines
          .filter((line) => line.included)
          .map((line) => ({
            componentId: line.componentId,
            value: line.value === '' ? null : Number(line.value),
            sequence: line.sequence,
          })),
      };
      return editing === null
        ? salaryStructuresApi.create(payload)
        : salaryStructuresApi.update(editing.id, payload);
    },
    onSuccess: (structure) => {
      toast.success(`${structure.name} saved`);
      close();
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'ACTIVE' | 'INACTIVE' }) =>
      salaryStructuresApi.setStatus(id, status),
    onSuccess: (structure) => {
      toast.success(`${structure.name} is now ${structure.status.toLowerCase()}`);
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const open = (structure: SalaryStructure | null) => {
    setEditing(structure);
    setCreating(structure === null);
    setCode(structure?.code ?? '');
    setName(structure?.name ?? '');
    setDescription(structure?.description ?? '');
    setMinBasic(structure?.minBasicSalary === undefined ? '' : String(structure.minBasicSalary));
    setMaxBasic(structure?.maxBasicSalary === undefined ? '' : String(structure.maxBasicSalary));

    const existing = new Map((structure?.components ?? []).map((line) => [line.componentId, line]));
    setLines(
      (components.data ?? []).map((component) => {
        const line = existing.get(component.id);
        return {
          componentId: component.id,
          // A new package starts with everything ticked: that is what the
          // server does with an empty list, and it cannot silently omit a
          // statutory deduction.
          included: structure === null ? true : line !== undefined,
          value: line !== undefined && !line.usingDefault ? String(line.value ?? '') : '',
          sequence: line?.sequence ?? component.displayOrder,
        };
      }),
    );
  };

  const close = () => {
    setEditing(null);
    setCreating(false);
    setLines([]);
  };

  const columns: Column<SalaryStructure>[] = [
    {
      key: 'name',
      header: 'Package',
      render: (row) => (
        <span>
          <span className="block text-sm font-medium text-ink">{row.name}</span>
          <span className="block font-mono text-[11px] text-subtle">{row.code}</span>
        </span>
      ),
    },
    {
      key: 'range',
      header: 'Basic salary range',
      render: (row) =>
        row.minBasicSalary === undefined && row.maxBasicSalary === undefined ? (
          <span className="text-subtle">—</span>
        ) : (
          <span className="text-sm text-ink">
            <Money value={row.minBasicSalary} /> – <Money value={row.maxBasicSalary} />
          </span>
        ),
    },
    {
      key: 'components',
      header: 'Components',
      render: (row) => (
        <span className="text-[13px] text-muted">
          {row.components.length} ·{' '}
          {row.components.filter((line) => line.componentType === 'EARNING').length} earning,{' '}
          {row.components.filter((line) => line.componentType === 'DEDUCTION').length} deduction
        </span>
      ),
    },
    {
      key: 'assigned',
      header: 'On this package',
      className: 'text-right',
      render: (row) => <span className="text-[13px] text-muted">{row.assignedCount}</span>,
    },
    { key: 'status', header: 'Status', render: (row) => <SalaryStatusBadge status={row.status} /> },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      render: (row) =>
        manages ? (
          <span className="flex justify-end gap-1.5">
            <Button size="sm" variant="secondary" onClick={() => open(row)}>
              Edit
            </Button>
            {/* Retiring one with staff on it is refused: move them first. */}
            {row.assignedCount === 0 && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() =>
                  setStatus.mutate({
                    id: row.id,
                    status: row.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE',
                  })
                }
              >
                {row.status === 'ACTIVE' ? 'Retire' : 'Restore'}
              </Button>
            )}
          </span>
        ) : null,
    },
  ];

  return (
    <div>
      <PageHeader
        title="Salary Packages"
        description="What a school hires against: a named bundle of components, with a basic salary set per person."
        icon={<Library className="h-5 w-5" aria-hidden="true" />}
        action={manages ? <Button onClick={() => open(null)}>Add a package</Button> : undefined}
      />

      <Card className="overflow-hidden">
        <DataTable
          columns={columns}
          rows={structures.data}
          rowKey={(row) => row.id}
          loading={structures.isPending}
          error={structures.error}
          onRetry={() => void structures.refetch()}
          emptyTitle="No salary packages"
          emptyDescription="A workable set is created the first time this page is opened."
        />
      </Card>

      {(editing !== null || creating) && (
        <Modal
          open
          onClose={close}
          title={editing === null ? 'Add a salary package' : `Edit ${editing.name}`}
          size="lg"
        >
          <form
            className="space-y-3"
            onSubmit={(event) => {
              event.preventDefault();
              save.mutate();
            }}
          >
            <div className="grid gap-3 sm:grid-cols-2">
              <TextField
                label="Code"
                required
                disabled={editing !== null}
                value={code}
                onChange={(event) => setCode(event.target.value)}
                placeholder="TEACH_STD"
                hint={editing !== null ? 'Fixed once created' : undefined}
              />
              <TextField
                label="Name"
                required
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Teaching"
              />
            </div>

            <TextField
              label="Description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />

            <div className="grid gap-3 sm:grid-cols-2">
              <TextField
                label="Minimum basic salary"
                type="number"
                min="0"
                value={minBasic}
                onChange={(event) => setMinBasic(event.target.value)}
                hint="Advisory — an assignment outside it warns rather than fails"
              />
              <TextField
                label="Maximum basic salary"
                type="number"
                min="0"
                value={maxBasic}
                onChange={(event) => setMaxBasic(event.target.value)}
              />
            </div>

            <fieldset className="border border-line">
              <legend className="px-2 text-[11px] font-semibold uppercase tracking-[0.05em] text-muted">
                Components in this package
              </legend>
              <div className="max-h-72 overflow-y-auto">
                {lines.map((line) => {
                  const component = componentsById.get(line.componentId);
                  if (component === undefined) {
                    return null;
                  }
                  const percentage = component.calculationMethod !== 'FIXED';
                  return (
                    <div
                      key={line.componentId}
                      className="flex items-center gap-3 border-b border-line px-3 py-2 last:border-0"
                    >
                      <input
                        type="checkbox"
                        checked={line.included}
                        // Statutory components are not the school's to remove.
                        disabled={component.statutory}
                        onChange={(event) =>
                          setLines(
                            lines.map((entry) =>
                              entry.componentId === line.componentId
                                ? { ...entry, included: event.target.checked }
                                : entry,
                            ),
                          )
                        }
                        className="h-4 w-4 shrink-0 border-line"
                        aria-label={`Include ${component.name}`}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="flex flex-wrap items-center gap-1.5">
                          <span className="text-sm text-ink">{component.name}</span>
                          <Badge tone={component.componentType === 'EARNING' ? 'success' : 'danger'}>
                            {component.componentType === 'EARNING' ? 'Earning' : 'Deduction'}
                          </Badge>
                          {component.statutory && <Badge tone="warning">Statutory</Badge>}
                        </span>
                        <span className="block text-[11px] text-subtle">
                          Default:{' '}
                          {component.defaultValue === undefined
                            ? '—'
                            : percentage
                              ? formatRate(component.defaultValue)
                              : component.defaultValue}
                        </span>
                      </span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={line.value}
                        disabled={!line.included}
                        placeholder="default"
                        onChange={(event) =>
                          setLines(
                            lines.map((entry) =>
                              entry.componentId === line.componentId
                                ? { ...entry, value: event.target.value }
                                : entry,
                            ),
                          )
                        }
                        className="w-24 shrink-0 border border-line bg-surface px-2 py-1 text-right text-sm text-ink disabled:bg-surface-3 disabled:text-subtle"
                        aria-label={`Value of ${component.name} in this package`}
                      />
                      <span className="w-6 shrink-0 text-[11px] text-subtle">
                        {percentage ? '%' : ''}
                      </span>
                    </div>
                  );
                })}
              </div>
            </fieldset>
            <p className="text-[12px] text-muted">
              Leave a value blank to use the component’s own default. Statutory components cannot be
              removed.
            </p>

            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="ghost" onClick={close}>
                Cancel
              </Button>
              <Button type="submit" disabled={save.isPending}>
                {save.isPending ? 'Saving…' : 'Save package'}
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
